import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Button, Checkbox } from '@mui/material';
import { CurrencyCode } from 'further-types/lp/vehicle';
import {
  numberToCurrencyString,
  numberToPercentageString,
  pluralize,
} from 'further-ui/utils';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useParams } from 'react-router-dom';
import api from 'lib/trpcClient';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import NumberInput from 'further-ui/components/NumberInput';
import { Row } from 'further-ui/components/Layout';

const NetEqualisationInterestPaymentTable: React.FC<{
  netEqualisationInterestPaymentBreakdown: Array<{
    commitmentId: string;
    investorName: string;
    equalisationInterestTotalLcy: number;
    equalisationInterestPercentage: number;
    aggregateTotalLcy: number;
    excludeFromEqualisationInterest?: boolean;
  }>;
  baseCurrency: CurrencyCode;
  totalEqualisationInterestLcy: number;
}> = ({
  netEqualisationInterestPaymentBreakdown,
  baseCurrency,
  totalEqualisationInterestLcy,
}) => {
  const { control, reset } = useFormContext();
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();
  const [equalisationInterest] = api.equalisations.getInterest.useSuspenseQuery(
    {
      equalisationId: id ?? '',
      vehicleId: selectedVehicleId ?? '',
    },
  );

  const rows = netEqualisationInterestPaymentBreakdown.map((item) => ({
    ...item,
    id: item.commitmentId,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'investorName',
      label: 'Name',
      weight: 0.2,
      renderAvatar: (row) => ({ size: 'lg', text: row.investorName }),
      footer: (
        <>
          Total from {rows.length} {pluralize('investor', rows.length)}
        </>
      ),
    },
    {
      key: 'aggregateTotalLcy',
      label: 'Aggregate equalisation payable / (receivable)',
      weight: 0.15,
      render: (row) =>
        numberToCurrencyString(row.aggregateTotalLcy, {
          fallback: '-',
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      key: 'equalisationInterestPercentage',
      label: 'Equalisation interest percentage',
      weight: 0.15,
      render: (row) =>
        numberToPercentageString(row.equalisationInterestPercentage, {
          forceDecimals: true,
          accuracy: 2,
        }),
    },
    {
      key: 'equalisationInterestTotalLcy',
      label: 'Equalisation interest payable / (receivable)',
      weight: 0.15,
      render: (row, rowIndex) => (
        <Controller
          control={control}
          name={`equalisationInterestPaymentBreakdown.${rowIndex}.equalisationInterestTotalLcy`}
          render={({ field }) => (
            <NumberInput
              fullWidth
              decimals={2}
              fixedDecimalScale
              currencyStartAdornment
              currency={baseCurrency}
              disabled={row.excludeFromEqualisationInterest}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      ),
      footer: numberToCurrencyString(totalEqualisationInterestLcy, {
        currency: baseCurrency,
        negativeInBrackets: true,
      }),
    },
    {
      key: 'excludeFromEqualisationInterest',
      label: 'Exclude',
      centered: true,
      width: '80px',
      render: (_row, rowIndex) => (
        <Controller
          render={({ field }) => (
            <Checkbox
              size="small"
              checked={field.value}
              onChange={field.onChange}
            />
          )}
          control={control}
          name={`equalisationInterestPaymentBreakdown.${rowIndex}.excludeFromEqualisationInterest`}
        />
      ),
    },
  ];

  return (
    <>
      <Table columns={columns} rows={rows} />
      <Row fullWidth justify="flex-end">
        <Button
          variant="outlined"
          onClick={() =>
            reset({
              equalisationInterestPaymentBreakdown:
                equalisationInterest.netEqualisationInterestPaymentBreakdown.map(
                  (item) => ({
                    ...item,
                    excludeFromEqualisationInterest: false,
                  }),
                ),
            })
          }
        >
          Reset interest totals
        </Button>
      </Row>
    </>
  );
};

export default NetEqualisationInterestPaymentTable;
